import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Privacy Policy"
        description="Read Ixoraly's privacy policy. We are committed to protecting your personal data and ensuring transparency in how we handle your information."
        path="/privacy-policy"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <Shield className="w-8 h-8 text-coral-500" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="privacy-heading">
              Privacy Policy
            </h1>
            <p className="text-gray-300">Last updated: February 2026</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Your privacy is important to us. This Privacy Policy explains how <strong>Ixoraly</strong> collects, uses, and protects your personal information when you use our press release distribution services.
            </p>

            <div className="bg-navy-50 rounded-xl p-8 mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span><strong>Personal details</strong> (name, email, phone, company name) when you create an account or submit a press release.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span><strong>Payment information</strong> when you purchase distribution services (processed securely via third-party payment gateways).</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span><strong>Technical data</strong> such as IP address, browser type, and cookies for analytics and site functionality.</span>
                </li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700">To process and publish press release submissions</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700">To communicate about your account and services</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700">To improve our platform and user experience</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700">To comply with legal obligations</p>
                </div>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Sharing of Information</h2>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>We do not sell, rent, or trade your personal data to third parties.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>Information may be shared with trusted service providers (e.g., payment processors, email tools) strictly for operational purposes.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>We may disclose information if required by law or legal process.</span>
                </li>
              </ul>
            </div>

            <div className="bg-navy-50 rounded-xl p-8 mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-700">
                We use industry-standard measures to protect your information against unauthorized access, alteration, or disclosure. Your data is encrypted in transit and at rest using modern encryption protocols.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Cookies</h2>
              <p className="text-gray-700 mb-4">
                Ixoraly uses cookies to enhance user experience, track website performance, and deliver personalized content. You can manage or disable cookies via your browser settings.
              </p>
              <Link to="/cookie-policy" className="text-navy-600 hover:text-navy-700 font-medium inline-flex items-center">
                Read our Cookie Policy <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. User Rights</h2>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>You have the right to request access, correction, or deletion of your personal data.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-3">•</span>
                  <span>To exercise these rights, contact us at <strong>privacy@ixoraly.com</strong>.</span>
                </li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Updates to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.
              </p>
            </div>

            <div className="bg-gradient-to-br from-navy-600 to-navy-700 rounded-xl p-8 text-white">
              <h3 className="font-bold text-xl mb-3">Questions or Concerns?</h3>
              <p className="text-navy-100 mb-4">
                If you have any questions about this Privacy Policy, please contact us.
              </p>
              <a href="mailto:privacy@ixoraly.com" className="inline-flex items-center px-6 py-3 bg-white text-navy-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                privacy@ixoraly.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
